package dev.kuml.portal.web.editor

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.DslResult
import dev.kuml.portal.web.api.GenerateResult
import dev.kuml.portal.web.api.GenerationCommentResponse
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.api.RenderResult
import dev.kuml.portal.web.api.revokeBlobUrlIfAny
import dev.kuml.portal.web.components.avatarWidget
import dev.kuml.portal.web.error.ErrorCopy
import dev.kuml.portal.web.i18n.t
import dev.kuml.portal.web.projects.DEFAULT_PROJECT_NAME
import dev.kuml.portal.web.projects.DefaultNameDisplay
import dev.kuml.portal.web.share.shareDialog
import dev.kuml.portal.web.usage.UsageFormat
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextAreaInput
import io.kvision.form.text.TextInput
import io.kvision.form.text.textAreaInput
import io.kvision.form.text.textInput
import io.kvision.html.Button
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import io.kvision.html.span
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.AddEventListenerOptions
import org.w3c.dom.Element
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.events.WheelEvent
import org.w3c.dom.pointerevents.PointerEvent
import org.w3c.files.File
import kotlin.coroutines.resume

/** R-22: lightweight client-side mirror of the server's `DiagramDefaultName` scheme ("Diagramm
 * N") -- used by [Container.editorScreen]'s `applyAutoName` to decide whether a diagram's tab
 * name still looks untouched. */
private val defaultDiagramTabNamePattern = Regex("^Diagramm \\d+$")

private fun isDefaultDiagramTabName(name: String): Boolean = defaultDiagramTabNamePattern.matches(name)

/** R-28: `Date.now()` -- this screen's own copy of the same trivial wrapper `CollabClient.kt` used
 * to carry before its presence payload moved to [PresencePayload] (see that class's own history). */
private fun jsNowMillis(): Double = kotlin.js.Date.now()

/**
 * Returned by [Container.editorScreen] so [dev.kuml.portal.web.PortalApp] can tear down whatever
 * live resources the editor screen opened (currently: the collaboration [CollabClient]) BEFORE the
 * next screen is mounted -- see `PortalApp.kt`'s `renderScreen`, which calls [dispose] right before
 * its `rootPanel.removeAll()`. `Router.kt` itself has no mount/unmount concept at all (it only
 * resolves a route to a [dev.kuml.portal.web.routing.Screen] and calls back) -- `PortalApp` is the
 * only place a screen transition is actually observable, which is why the dispose hook lives here
 * rather than in the router.
 */
public class EditorScreenHandle(
    private val onDispose: () -> Unit,
) {
    public fun dispose() {
        onDispose()
    }
}

/**
 * Project editor (`/projects/:id`, design spec section 3) -- the diagram stage (left, or above on
 * narrow viewports) and the prompt column (right, or below). This is where mustFix 1 and mustFix 2
 * live: the created-vs-updated success copy, and the layout-independent 204 empty-state hint.
 *
 * @param onBack navigates to `/projects`.
 * @param onUsageChanged called right after a successful generation so the header's usage chip can
 *   refresh without waiting for the next navigation (see [dev.kuml.portal.web.components.PortalHeader]).
 * @return an [EditorScreenHandle] the caller must [EditorScreenHandle.dispose] before tearing this
 *   screen's DOM down (see that class's KDoc) -- closes the live [CollabClient] connection.
 */
public fun Container.editorScreen(
    api: PortalApi,
    projectId: String,
    diagramId: String,
    onBack: () -> Unit,
    onSwitchDiagram: (String) -> Unit,
    onUsageChanged: () -> Unit,
): EditorScreenHandle {
    lateinit var backButton: Button
    lateinit var nameDiv: Div
    lateinit var diagramTabsHost: Div
    lateinit var collabIndicatorDiv: Div
    lateinit var collabAvatarsDiv: Div
    lateinit var stage: Div
    lateinit var inspectorHost: Div
    lateinit var promptTextArea: TextAreaInput
    lateinit var generateButton: Button
    lateinit var cancelButton: Button
    lateinit var statusDiv: Div
    lateinit var statusDetailDiv: Div
    lateinit var dslToggle: Div
    lateinit var dslContent: Div

    // ---------- R-9: report-as-incorrect + comment thread ----------
    lateinit var reportRow: Div
    lateinit var reportFormRow: Div
    lateinit var reportThreadRow: Div

    // ---------- R-8: attach-a-context-file state ----------
    lateinit var attachButton: Button
    lateinit var attachChipRow: Div
    lateinit var attachFileInput: HTMLInputElement

    var currentProjectName = DEFAULT_PROJECT_NAME
    var manuallyRenamed = false
    // R-22: the CURRENTLY OPEN diagram's tab name -- separate from currentProjectName above.
    // Downloads/auto-naming target this, not the project name (see applyAutoName below).
    var currentDiagramName = ""
    lateinit var diagramTabsHandle: DiagramTabsHandle
    var diagramExistedBefore = false
    var currentSvgBlobUrl: String? = null
    // R-24: `null` = not loaded (or invalidated by a server-side diagram change) -- distinct from
    // `""`, which would be indistinguishable from "loaded, and it happens to be empty". See
    // [invalidateDsl]/[loadDslIfNeeded] below.
    var currentDsl: String? = null
    var dslExpanded = false
    var generating = false
    var generationToken = 0
    var focusPromptOnFirstEmptyRender = true
    var attachedContextId: String? = null
    var attachedContextLabel: String? = null

    // ---------- R-9: report-as-incorrect + comment thread state ----------
    var currentGenerationId: String? = null
    var currentGenerationReported = false

    // ---------- Zoom/pan state (R-6) ----------
    // `zoomWrapper` is nullable (not `lateinit`) because `showDiagram()`/`showEmptyStage()` tear
    // down and rebuild `stage`'s children on every state change (see the class-level KDoc on that
    // convention) -- there is no diagram, and therefore no wrapper, until the first successful
    // `showDiagram()` call. `setDimmed()` below relies on this being `null` to safely no-op.
    // `zoomToolbar` (R-7) shares this exact lifecycle: built fresh in `showDiagram()`, torn down
    // (implicitly, via `stage.removeAll()`) and nulled out in `resetZoomState()`.
    var zoomWrapper: Div? = null
    var zoomToolbar: ZoomToolbarHandle? = null
    var scale = 1.0
    var fitScale = 1.0
    var translateX = 0.0
    var translateY = 0.0
    var naturalWidth = 0.0
    var naturalHeight = 0.0
    var isPanning = false
    var panStartX = 0.0
    var panStartY = 0.0
    var panOriginX = 0.0
    var panOriginY = 0.0

    // ---------- R-27: interactive diagram-editing state ----------
    // `selection`/`latestOutline` are the two pure inputs `InspectorModel.contentFor` needs --
    // kept as plain closure-captured `var`s exactly like every other piece of this screen's mutable
    // state (see the class-level KDoc's established convention), not a dedicated state object.
    // `inlineSvgRoot` is `null` whenever the current diagram is showing as the pre-R-27 `<img>`
    // fallback (see `showDiagram`'s KDoc) -- the stage's persistent click listener below no-ops
    // entirely in that case, so a fallback render is simply non-interactive, never crashes.
    var selection = SelectionState()
    var latestOutline = DiagramOutlineResponse(elements = emptyList())
    var inlineSvgRoot: Element? = null
    lateinit var inspectorHandle: InspectorPanelHandle
    // Assigned once, near the bottom of this function, right where the pre-R-27 `val collab = ...`
    // used to sit -- declared `lateinit var` up here instead because the inspector panel's `onOp`
    // callback (built during the "Layout" section below, well before the collaboration connection
    // itself is constructed) needs to close over it. Never invoked before assignment: `onOp` only
    // ever fires from a user click, which cannot happen until this whole function has returned and
    // the screen is live in the DOM -- by which point `collab` is always already assigned.
    lateinit var collab: CollabClient

    // ---------- R-28: presence cursor/selection sharing ----------
    // Every var here is a plain closure-captured mutable, exactly like every other piece of this
    // screen's state (see the class-level KDoc's established convention) -- no dedicated state
    // object. `presenceState` survives `showDiagram()`/`resetZoomState()` rebuilds (it is PEER
    // state, not a DOM reference) -- exactly like `latestOutline` -- while `presenceOverlay` does
    // NOT (see `resetZoomState`'s own comment on this distinction; a bug here is easy to write and
    // easy to miss, see the R-28 plan's "Stolperfallen").
    var presenceState: Map<String, PeerPresence> = emptyMap()
    var presenceOverlay: PresenceOverlayHandle? = null
    var schedulerState = SchedulerState()
    var pendingPresenceTimer: Int? = null
    var idleTimer: Int? = null
    var presenceTickTimer: Int? = null
    // The last pointer position this screen has SEEN in viewBox units, regardless of whether it was
    // actually sent yet (a `ScheduleTrailing`/`Suppress` decision still updates this) -- what the
    // trailing-edge timer callback below sends when it fires, and what `setSelection`'s own presence
    // hook sends alongside a selection-only update. `null` after a leave/idle/dispose, exactly
    // mirroring what an outgoing payload's absent `c` field means.
    var latestPointerViewBoxPos: Pair<Double, Double>? = null
    // The peer list `onPeersChanged` last delivered -- kept so `onPeerPresenceChanged`'s per-token
    // repaint/avatar-rebuild path (fired far more often, on every presence frame) never needs its
    // own separate identity bookkeeping; see `renderCollabAvatars` below, extracted out of
    // `onPeersChanged` so both callbacks can call it.
    var latestPeers: List<CollabPeer> = emptyList()

    fun setStatusNeutral(message: String) {
        statusDiv.content = message
        statusDiv.removeCssClass("kportal-status-error")
        statusDiv.removeCssClass("kportal-status-success")
        statusDiv.addCssClass("kportal-status-neutral")
        statusDetailDiv.content = ""
    }

    fun setStatusError(
        message: String,
        detail: String?,
    ) {
        statusDiv.content = message
        statusDiv.removeCssClass("kportal-status-neutral")
        statusDiv.removeCssClass("kportal-status-success")
        statusDiv.addCssClass("kportal-status-error")
        statusDetailDiv.content = detail ?: ""
    }

    fun setStatusSuccess(message: String) {
        statusDiv.content = "✓ $message"
        statusDiv.removeCssClass("kportal-status-neutral")
        statusDiv.removeCssClass("kportal-status-error")
        statusDiv.addCssClass("kportal-status-success")
        statusDetailDiv.content = ""
    }

    fun updateDslContent(dsl: String) {
        currentDsl = dsl
        dslContent.content = dsl
    }

    /** Lädt die gespeicherte DSL genau dann nach, wenn das Panel offen ist und nichts im Cache
     * liegt -- also nie für Nutzer, die das Panel nie öffnen. Der Cache wird von [invalidateDsl]
     * geleert, sobald sich der serverseitige Diagrammstand ändert (Mount, Collab-Refresh, R-22-
     * Tab-Wechsel via Remount). */
    fun loadDslIfNeeded() {
        if (!DslPanelState.needsFetch(dslExpanded, currentDsl)) return
        dslContent.content = DslPanelState.loading(t.editor)
        AppScope.launch {
            when (val result = api.diagramDsl(diagramId)) {
                is DslResult.Loaded -> updateDslContent(result.dsl)
                DslResult.Empty -> dslContent.content = DslPanelState.empty(t.editor)
                // Nicht-kritisch: der Ersatztext bleibt stehen, currentDsl bleibt null, damit ein
                // erneutes Auf-/Zuklappen einen neuen Versuch auslöst. Bewusst NICHT in der
                // aria-live-Statuszeile (das Panel ist eine Nebenansicht, kein Aktionsergebnis).
                is DslResult.HttpError, DslResult.NetworkError -> dslContent.content = DslPanelState.error(t.editor)
            }
        }
    }

    /** Verwirft den DSL-Cache, weil sich der serverseitige Diagrammstand geändert hat (Mount,
     * Collab-Refresh, R-22-Tab-Wechsel). Lädt sofort neu, falls das Panel gerade offen ist. */
    fun invalidateDsl() {
        currentDsl = null
        if (dslExpanded) loadDslIfNeeded() else dslContent.content = ""
    }

    // ---------- R-27: selection + inspector orchestration ----------

    /** Repaints the inspector panel for the CURRENT [selection]/[latestOutline] pair, and the
     * inlined SVG's highlight alongside it (a no-op for the highlight half when the current
     * render fell back to the non-interactive `<img>` display, see [inlineSvgRoot]'s KDoc).
     * Called after every selection change (a click, an Escape) AND after every outline refresh
     * (the outline itself, not just the selection, can change what [InspectorModel.contentFor]
     * produces -- e.g. a collaborator renamed the very element this connection has selected). */
    fun refreshInspector() {
        inspectorHandle.update(InspectorModel.contentFor(selection, latestOutline))
        inlineSvgRoot?.let { DiagramCanvas.applyHighlight(it, selection) }
    }

    // ---------- R-28: presence cursor/selection sharing orchestration ----------
    // Declared BEFORE `setSelection` below (Kotlin resolves a local function call against what is
    // textually already declared in its enclosing block, unlike top-level declarations) -- every
    // function here that `setSelection`'s own presence hook transitively needs
    // ([applyPresenceSchedulerDecision] -> [cancelPendingPresenceTimer]/
    // [armTrailingPresenceTimerIfNeeded] -> [currentPresenceSelectionIds]) must exist above it.

    /** The current selection, flattened into the one `List<String>` [PresencePayload.encode]'s `s`
     * field carries -- a node multi-select and an edge selection are mutually exclusive (see
     * [SelectionState]'s own KDoc), so at most one of the two contributes anything here. */
    fun currentPresenceSelectionIds(): List<String> = selection.nodeIds + listOfNotNull(selection.edgeId)

    /** The diagram's current viewBox as a [ViewBoxRect] -- [PresencePayload.decode]'s clamp target
     * for an incoming peer cursor. Falls back to `0 0 naturalWidth naturalHeight` exactly like
     * [naturalSizeOfInlinedSvg]'s own viewBox fallback, for the same reason (not every render's
     * inlined `<svg>` necessarily carries a well-formed `viewBox` attribute). */
    fun currentViewBoxRect(): ViewBoxRect {
        val parts = inlineSvgRoot?.getAttribute("viewBox")?.trim()?.split(Regex("[\\s,]+"))
        if (parts?.size == 4) {
            val minX = parts[0].toDoubleOrNull()
            val minY = parts[1].toDoubleOrNull()
            val width = parts[2].toDoubleOrNull()
            val height = parts[3].toDoubleOrNull()
            if (minX != null && minY != null && width != null && height != null) {
                return ViewBoxRect(minX, minY, width, height)
            }
        }
        return ViewBoxRect(0.0, 0.0, naturalWidth, naturalHeight)
    }

    fun cancelPendingPresenceTimer() {
        pendingPresenceTimer?.let { window.clearTimeout(it) }
        pendingPresenceTimer = null
    }

    /** Arms `EditorScreen.kt`'s ONE trailing-edge timer, only if none is currently pending (see
     * [SchedulerDecision.ScheduleTrailing]'s own KDoc on coalescing) -- when it fires, it sends
     * whatever [latestPointerViewBoxPos]/[currentPresenceSelectionIds] are CURRENT at that moment,
     * never a value frozen at arming time. */
    fun armTrailingPresenceTimerIfNeeded(delayMs: Double) {
        if (pendingPresenceTimer != null) return
        pendingPresenceTimer =
            window.setTimeout({
                pendingPresenceTimer = null
                val now = jsNowMillis()
                collab.sendPresence(PresencePayload.encode(latestPointerViewBoxPos, currentPresenceSelectionIds()))
                schedulerState = schedulerState.copy(lastSentAtMs = now, lastSentCursor = latestPointerViewBoxPos)
            }, delayMs.toInt())
    }

    /** The one place a [SchedulerDecision] is ever acted on -- every caller ([handlePresencePointerMove],
     * [setSelection]'s presence hook) goes through this rather than inlining the `when`, so the
     * "send now vs. arm a trailing timer vs. do nothing" handling can never drift between call sites. */
    fun applyPresenceSchedulerDecision(decision: SchedulerDecision) {
        when (decision) {
            is SchedulerDecision.SendNow -> {
                cancelPendingPresenceTimer()
                collab.sendPresence(PresencePayload.encode(latestPointerViewBoxPos, currentPresenceSelectionIds()))
                schedulerState = decision.nextState
            }
            is SchedulerDecision.ScheduleTrailing -> armTrailingPresenceTimerIfNeeded(decision.delayMs)
            SchedulerDecision.Suppress -> Unit
        }
    }

    fun clearPresenceIdleTimer() {
        idleTimer?.let { window.clearTimeout(it) }
        idleTimer = null
    }

    /** Sends an absent-cursor snapshot right now (always [SchedulerDecision.SendNow] per
     * [PresenceScheduler.onPointerLeaveOrIdle]'s own KDoc) -- shared by [handlePresencePointerLeave]
     * and [resetPresenceIdleTimer]'s own timeout callback, so the two never drift. */
    fun sendPresenceAbsentCursorNow() {
        val decision = PresenceScheduler.onPointerLeaveOrIdle(schedulerState, jsNowMillis())
        if (decision is SchedulerDecision.SendNow) {
            collab.sendPresence(PresencePayload.encode(null, currentPresenceSelectionIds()))
            schedulerState = decision.nextState
        }
    }

    /** (Re)starts the [PresenceScheduler.IDLE_TIMEOUT_MS] idle timer -- called on every pointer move
     * over the stage. A pointer that stops moving for that long without a `pointerleave` (e.g. the
     * user switches tabs/apps without the mouse ever crossing the stage boundary) is treated exactly
     * like one. */
    fun resetPresenceIdleTimer() {
        clearPresenceIdleTimer()
        idleTimer =
            window.setTimeout({
                idleTimer = null
                cancelPendingPresenceTimer()
                latestPointerViewBoxPos = null
                sendPresenceAbsentCursorNow()
            }, PresenceScheduler.IDLE_TIMEOUT_MS.toInt())
    }

    /** R-28: no `isPanning` guard (unlike [handlePointerMove]) -- cursor sharing runs regardless of
     * whether this connection is currently panning the stage, neither blocked by nor interfering
     * with that gesture. A no-op whenever [inlineSvgRoot] is `null` (the empty state, skeleton, or a
     * render that fell back to the non-interactive `<img>` display), matching [handleClick]'s own
     * guard for the same reason. */
    fun handlePresencePointerMove(e: PointerEvent) {
        val root = inlineSvgRoot ?: return
        val point = clientPointToViewBox(root, e.clientX.toDouble(), e.clientY.toDouble()) ?: return
        latestPointerViewBoxPos = point
        applyPresenceSchedulerDecision(PresenceScheduler.onPointerMove(schedulerState, jsNowMillis(), point))
        resetPresenceIdleTimer()
    }

    fun handlePresencePointerLeave(e: PointerEvent) {
        clearPresenceIdleTimer()
        cancelPendingPresenceTimer()
        latestPointerViewBoxPos = null
        sendPresenceAbsentCursorNow()
    }

    /** Rebuilds the collab indicator chip + avatar strip from [latestPeers]/[presenceState] --
     * extracted out of `onPeersChanged` (R-16) so [CollabClient]'s new `onPeerPresenceChanged`
     * callback (R-28) can also trigger it, gated on an actual SELECTION change for that peer (see
     * the call site below) so a 10 Hz cursor stream never rebuilds this DOM strip on every tick. */
    fun renderCollabAvatars() {
        collabIndicatorDiv.content = CollabPresenceFormat.indicatorText(latestPeers.size, t.editor) ?: ""
        collabAvatarsDiv.removeAll()
        latestPeers.take(CollabPresenceFormat.MAX_VISIBLE_AVATARS).forEach { peer ->
            val displayName = peer.displayName.ifEmpty { "?" }
            val elementName =
                presenceState[peer.tokenHex]?.selection?.firstOrNull()?.let { elementById(latestOutline, it)?.name }
            collabAvatarsDiv.avatarWidget(
                userId = peer.userId,
                displayName = displayName,
                cacheBuster = null,
                sizeClass = "kportal-avatar-sm",
                title = CollabPresenceFormat.avatarTitle(displayName, elementName, t.editor),
            )
        }
        CollabPresenceFormat.overflowText(latestPeers.size)?.let { overflow ->
            collabAvatarsDiv.span(overflow, className = "kportal-avatar-overflow")
        }
    }

    /** Repaints [presenceOverlay] for the CURRENT [presenceState]/[selection] pair -- a no-op
     * whenever [inlineSvgRoot] is `null` (see [handlePresencePointerMove]'s own guard for the same
     * reason: no interactive diagram, nothing to paint an overlay over). */
    fun repaintPresenceOverlay() {
        val root = inlineSvgRoot ?: return
        presenceOverlay?.repaint(presenceState, latestPeers.associateBy { it.tokenHex }, root, selection, scale, jsNowMillis())
    }

    // ---------- R-27: selection + inspector orchestration (continued) ----------

    /** The one place [selection] is ever reassigned -- every call site (the stage's click
     * listener, the inspector panel's "select this relationship" callback) goes through this
     * rather than assigning `selection` directly, so a selection change can never forget to
     * repaint. */
    fun setSelection(next: SelectionState) {
        selection = next
        refreshInspector()
        applyPresenceSchedulerDecision(PresenceScheduler.onSelectionChange(schedulerState, jsNowMillis()))
    }

    /**
     * Fetches `/outline` and updates [latestOutline]/[selection]/the inspector panel accordingly --
     * called from [refreshDiagramFromServer] (every collab refresh, tab switch, and initial mount)
     * AND, separately, right after a successful `runGeneration` (a fresh AI generation changes the
     * live model just as much as a collaborator's op does, even though [showDiagram] itself falls
     * back to the non-interactive `<img>` display for THAT one render -- see its KDoc). A failed
     * fetch degrades to an EMPTY outline and a CLEARED selection rather than leaving a stale one in
     * place -- a stale outline could make [InspectorModel.contentFor] resolve a selection against
     * ids that no longer mean what they did, exactly the kind of race [DiagramSelection.restore]
     * exists to guard against; failing closed to "nothing selected" is the safe default when this
     * connection cannot even find out what still exists.
     */
    suspend fun refreshOutline() {
        when (val result = api.diagramOutline(diagramId)) {
            is ApiResult.Success -> {
                latestOutline = result.value
                selection = DiagramSelection.restore(selection, allIdsOf(latestOutline))
            }
            is ApiResult.HttpError, ApiResult.NetworkError -> {
                latestOutline = DiagramOutlineResponse(elements = emptyList())
                selection = SelectionState()
            }
        }
        refreshInspector()
    }

    // ---------- Zoom/pan orchestration (R-6) ----------
    // Deliberately raw DOM (`getElement()`, `addEventListener`) rather than KVision's typed
    // `setEventListener<T>` DSL: wheel/pointer events aren't part of that DSL anywhere else in
    // this codebase (only click/blur/keydown/input are), and the pivot math needs the exact
    // `getBoundingClientRect()`/`clientX`/`clientY` values those raw event objects carry.

    fun resetZoomState() {
        zoomWrapper = null
        zoomToolbar = null
        scale = 1.0
        fitScale = 1.0
        translateX = 0.0
        translateY = 0.0
        naturalWidth = 0.0
        naturalHeight = 0.0
        isPanning = false
        // R-27: torn down alongside the wrapper it lived in -- the stage's click listener guards
        // on this being non-null, so clearing it here (called from both showDiagram AND
        // showEmptyStage) is what makes a click on the empty-state/skeleton view a safe no-op
        // rather than hit-testing a DOM node that no longer belongs to the current stage.
        inlineSvgRoot = null
        // R-28: torn down alongside `zoomWrapper` (its own DOM parent, see PresenceOverlay.create)
        // -- deliberately NOT `presenceState = emptyMap()` here. `presenceState` is PEER state, not
        // a DOM reference, and must survive a diagram rebuild exactly like `latestOutline` does --
        // clearing it here would make every peer cursor vanish on every collab refresh/tab switch
        // until that peer's next presence frame arrives (see the R-28 plan's "Stolperfallen").
        presenceOverlay = null
    }

    fun applyZoomTransform() {
        val wrapper = zoomWrapper ?: return
        val panning = ZoomMath.canPan(scale, fitScale)
        if (!panning) {
            // Snap back to centered/no-offset once zoomed back to (or below) fit -- never leave a
            // diagram parked off-screen, and keep the internal state consistent with what's
            // actually painted before the next zoom/pan action computes its pivot math from it.
            translateX = 0.0
            translateY = 0.0
        }
        wrapper.getElement()?.let { it.style.transform = "translate(${translateX}px, ${translateY}px) scale($scale)" }
        if (panning) stage.addCssClass("kportal-zoom-pannable") else stage.removeCssClass("kportal-zoom-pannable")
        zoomToolbar?.updateZoomState(scale, fitScale)
        // R-28: the overlay's cursor-icon/flag counter-scale and ring-offset stack both depend on
        // `scale` (see PresenceOverlayHandle.repaint's own KDoc) -- repainted on every zoom step so
        // they never lag one frame behind the diagram's own transform.
        repaintPresenceOverlay()
    }

    fun zoomFit() {
        zoomWrapper ?: return
        val rect = stage.getElement()?.getBoundingClientRect() ?: return
        fitScale = ZoomMath.computeFitScale(rect.width, rect.height, naturalWidth, naturalHeight)
        scale = fitScale
        translateX = 0.0
        translateY = 0.0
        applyZoomTransform()
    }

    fun zoomTo(
        candidateScale: Double,
        pivotClientX: Double? = null,
        pivotClientY: Double? = null,
    ) {
        zoomWrapper ?: return
        val rect = stage.getElement()?.getBoundingClientRect() ?: return
        val pivotX = (pivotClientX ?: (rect.left + rect.width / 2)) - rect.left
        val pivotY = (pivotClientY ?: (rect.top + rect.height / 2)) - rect.top
        val result = ZoomMath.zoomTo(scale, translateX, translateY, candidateScale, fitScale, pivotX, pivotY)
        if (!result.changed) return
        scale = result.scale
        translateX = result.translateX
        translateY = result.translateY
        applyZoomTransform()
    }

    fun handleWheel(e: WheelEvent) {
        zoomWrapper ?: return
        // Only hijack the wheel when the user is explicitly asking to zoom (Ctrl/Cmd+wheel, the
        // standard trackpad-pinch-to-zoom signal) or is already zoomed past fit -- otherwise let
        // the page scroll normally instead of trapping the user's scroll gesture in the diagram.
        if (!(e.ctrlKey || e.metaKey || ZoomMath.canPan(scale, fitScale))) return
        e.preventDefault()
        val direction = if (e.deltaY < 0) ZoomMath.ZOOM_STEP else 1.0 / ZoomMath.ZOOM_STEP
        zoomTo(scale * direction, e.clientX.toDouble(), e.clientY.toDouble())
    }

    fun handlePointerDown(e: PointerEvent) {
        zoomWrapper ?: return
        if (!ZoomMath.canPan(scale, fitScale)) return
        if (e.button.toInt() != 0) return
        isPanning = true
        panStartX = e.clientX.toDouble()
        panStartY = e.clientY.toDouble()
        panOriginX = translateX
        panOriginY = translateY
        stage.getElement()?.setPointerCapture(e.pointerId)
        stage.addCssClass("kportal-zoom-panning")
        e.preventDefault()
    }

    fun handlePointerMove(e: PointerEvent) {
        if (!isPanning) return
        translateX = panOriginX + (e.clientX - panStartX)
        translateY = panOriginY + (e.clientY - panStartY)
        applyZoomTransform()
    }

    fun handlePointerUp(e: PointerEvent) {
        if (!isPanning) return
        isPanning = false
        stage.removeCssClass("kportal-zoom-panning")
        try {
            stage.getElement()?.releasePointerCapture(e.pointerId)
        } catch (ignored: Throwable) {
            // Already released -- e.g. pointercancel fired after the browser itself released capture.
        }
    }

    /** R-27: click-to-select -- a no-op whenever [inlineSvgRoot] is `null` (the empty state, the
     * skeleton, or a render that fell back to the non-interactive `<img>` display, see
     * [showDiagram]'s KDoc), so a click anywhere on the stage is always safe regardless of what it
     * currently shows. `ZoomToolbar.kt`'s `STAGE_EVENTS_TO_SWALLOW` stops a toolbar button's own
     * click from ever reaching this listener at all -- see that file's R-27 addition.
     *
     * Bump to kUML 0.54.1: a direct hit ([DiagramCanvas.idAt]) resolving to a known node or edge id
     * is used as-is; anything else (background, or an id the outline has never heard of) falls back
     * to [DiagramCanvas.resolveEdgeByTolerance] -- a small radial tolerance search that can only ever
     * resolve to an EDGE, never a node -- before finally treating the click as a background click.
     *
     * **Review fix (2026-09-13):** [directId] is now threaded through to
     * [DiagramCanvas.clickSelectionFor] alongside [resolvedId], instead of only ever inspecting
     * [resolvedId] -- see that function's KDoc for why collapsing "true background" and "known-but-
     * unrecognized id" into the same `null` was a regression on a comment click. */
    fun handleClick(e: MouseEvent) {
        inlineSvgRoot ?: return
        val target = e.target as? Element ?: return
        val directId = DiagramCanvas.idAt(target)
        val resolvedId =
            when {
                directId != null && directId in relationshipIdsOf(latestOutline) -> directId
                directId != null && directId in nodeIdsOf(latestOutline) -> directId
                else -> DiagramCanvas.resolveEdgeByTolerance(latestOutline, e.clientX.toDouble(), e.clientY.toDouble())
            }
        setSelection(DiagramCanvas.clickSelectionFor(selection, latestOutline, directId, resolvedId, e.shiftKey))
    }

    /** R-27: loads [src] into a fresh, off-DOM `<img>` and suspends until it either loads or
     * errors -- `null` for the latter. Decouples [exportPng] from whatever is CURRENTLY displayed
     * in [zoomWrapper] (a live `<img>` before this wave, now possibly an inlined `<svg>` instead,
     * see [showDiagram]'s KDoc) -- [rasterizeSvgImageToPng] itself is unchanged, it has always only
     * ever needed an [HTMLImageElement], never specifically the one on screen. */
    suspend fun loadOffscreenImage(src: String): HTMLImageElement? =
        suspendCancellableCoroutine { cont ->
            val offscreen = document.createElement("img") as HTMLImageElement
            // Exactly one of "load"/"error" ever fires for a given `src` -- no `cont.isActive`
            // guard needed, matching `DiagramDownload.kt`'s own `rasterizeSvgImageToPng`'s
            // unconditional `cont.resume(...)` calls in its `toBlob` callback/`catch` block.
            offscreen.addEventListener("load", { cont.resume(offscreen) })
            offscreen.addEventListener("error", { cont.resume(null) })
            offscreen.src = src
        }

    /** R-7's PNG export: rasterises the diagram client-side (see [loadOffscreenImage]/
     * `rasterizeSvgImageToPng`) and hands the blob to the browser's download. Failure is surfaced in
     * the existing `aria-live` status area with an actionable fallback ("use SVG instead") rather
     * than being swallowed -- the user just clicked a button and must learn that nothing happened. */
    fun exportPng(
        svgBlobUrl: String,
        toolbar: ZoomToolbarHandle,
    ) {
        toolbar.setPngBusy(true)
        AppScope.launch {
            val offscreen = loadOffscreenImage(svgBlobUrl)
            val blob = offscreen?.let { rasterizeSvgImageToPng(it) }
            toolbar.setPngBusy(false)
            if (blob == null) {
                setStatusError(t.editor.pngExportFailed, t.editor.pngExportFailedDetail)
            } else {
                triggerBlobDownload(blob, DiagramFileName.forDiagramName(currentDiagramName, "png", t.editor.diagramFileFallback))
            }
        }
    }

    /** The natural (unscaled) pixel size [zoomFit]/[applyZoomTransform] need, read off an inlined
     * SVG's own `width`/`height` attributes (falling back to its `viewBox`'s third/fourth numbers
     * for a render that only sets one, not the other) -- the DOM-equivalent, for a real `<svg>`
     * element, of an `<img>`'s `naturalWidth`/`naturalHeight`, which no longer applies once the
     * diagram is no longer displayed as an `<img>` at all. `null` when neither attribute yields a
     * usable size -- [showDiagram] treats that exactly like [SvgInliner.inline] itself returning
     * `null`: fall back to the non-interactive `<img>` display for this render, rather than show an
     * interactive diagram [zoomFit] can never size correctly. */
    fun naturalSizeOfInlinedSvg(root: Element): Pair<Double, Double>? {
        fun parseLength(raw: String?): Double? = raw?.trim()?.takeWhile { it.isDigit() || it == '.' }?.toDoubleOrNull()
        val width = parseLength(root.getAttribute("width"))
        val height = parseLength(root.getAttribute("height"))
        if (width != null && height != null) return width to height
        val viewBoxParts = root.getAttribute("viewBox")?.trim()?.split(Regex("[\\s,]+"))
        if (viewBoxParts?.size == 4) {
            val vbWidth = viewBoxParts[2].toDoubleOrNull()
            val vbHeight = viewBoxParts[3].toDoubleOrNull()
            if (vbWidth != null && vbHeight != null) return vbWidth to vbHeight
        }
        return null
    }

    /**
     * R-27: attempts an interactive, click-to-select inline `<svg>` display (via [SvgInliner],
     * [rawSvg]) and falls back to the pre-R-27 non-interactive `<img src="blob:...">` display
     * whenever that is not possible -- [SvgInliner.inline] itself returning `null` (the K-4
     * fail-safe, or an unparseable payload), [naturalSizeOfInlinedSvg] being unable to determine a
     * size, or [rawSvg] itself being `null` (the caller has no SVG text in hand at all -- see
     * [dev.kuml.portal.web.api.RenderResult.Rendered]'s KDoc; `runGeneration`'s `GenerateResult`
     * carries no raw SVG text, only [svgBlobUrl], so a fresh AI generation always displays via the
     * `<img>` fallback until the next outline-bearing refresh makes it interactive). Either path
     * ends up inside the SAME [zoomWrapper] -- zoom/pan/dim (see [applyZoomTransform]/[setDimmed])
     * operate on that wrapper regardless of which child it holds, and [exportPng] no longer cares
     * either (it rasterises straight from [svgBlobUrl] via its own off-DOM `<img>`, see
     * [loadOffscreenImage]).
     */
    fun showDiagram(
        svgBlobUrl: String,
        rawSvg: String?,
    ) {
        val previous = currentSvgBlobUrl
        stage.removeAll()
        resetZoomState()

        val wrapper = stage.div(className = "kportal-zoom-wrapper")
        zoomWrapper = wrapper

        val inlined = rawSvg?.let { SvgInliner.inline(it) }
        val inlinedSize = inlined?.let { naturalSizeOfInlinedSvg(it) }

        if (inlined != null && inlinedSize != null) {
            wrapper.getElement()?.appendChild(inlined)
            inlineSvgRoot = inlined
            naturalWidth = inlinedSize.first
            naturalHeight = inlinedSize.second
            zoomFit()
            DiagramCanvas.applyHighlight(inlined, selection)
            // R-28: only for the interactive inline-SVG path -- an `<img>` fallback (the `else`
            // branch below) is never click-to-select either, so a presence overlay over it would be
            // dead weight with nothing to draw its selection rings against.
            val viewBoxAttr = inlined.getAttribute("viewBox") ?: "0 0 $naturalWidth $naturalHeight"
            presenceOverlay = PresenceOverlay.create(wrapper, viewBoxAttr, naturalWidth, naturalHeight)
            repaintPresenceOverlay()
        } else {
            val img = wrapper.image(src = svgBlobUrl, alt = t.editor.diagramAlt)

            fun applyNaturalSizeAndFit(element: HTMLImageElement) {
                naturalWidth = element.naturalWidth.toDouble()
                naturalHeight = element.naturalHeight.toDouble()
                zoomFit()
            }
            // `addAfterInsertHook` (not a bare `img.getElement()` right here) so `img`'s real DOM
            // node is guaranteed to exist before checking `.complete`/attaching the `load`
            // listener -- KVision's virtual-DOM patch for this freshly-created `<img>` may not
            // have run yet.
            img.addAfterInsertHook {
                val element = img.getElement() as? HTMLImageElement ?: return@addAfterInsertHook
                if (element.complete) {
                    applyNaturalSizeAndFit(element)
                } else {
                    element.addEventListener("load", { applyNaturalSizeAndFit(element) })
                }
            }
        }

        // R-7 toolbar: built here (not in the static layout) so it shares the diagram's lifecycle --
        // no "Download PNG" control in the empty state, and every callback closes over THIS
        // diagram's blob URL rather than over mutable editor state a concurrent regeneration could
        // have replaced by the time the user clicks.
        zoomToolbar =
            stage.zoomToolbar(
                onZoomIn = { zoomTo(scale * ZoomMath.ZOOM_STEP) },
                onZoomOut = { zoomTo(scale / ZoomMath.ZOOM_STEP) },
                onZoomFit = { zoomFit() },
                onDownloadSvg = {
                    // Lossless: the blob URL already holds the server's SVG bytes verbatim (see
                    // PortalApi.toSvgBlobUrl) -- no re-serialisation, and deliberately no revoke
                    // here (this URL may still be the live <img> source).
                    triggerBlobUrlDownload(
                        svgBlobUrl,
                        DiagramFileName.forDiagramName(currentDiagramName, "svg", t.editor.diagramFileFallback),
                    )
                },
                onDownloadPng = { zoomToolbar?.let { toolbar -> exportPng(svgBlobUrl, toolbar) } },
            )

        currentSvgBlobUrl = svgBlobUrl
        revokeBlobUrlIfAny(previous)
    }

    /** Dims the whole [zoomWrapper] (whichever of `<img>`/inline `<svg>` it currently holds) rather
     * than reaching into its children for a specific `Image` widget -- unlike the pre-R-27 version,
     * [zoomWrapper] does not always hold one anymore (see [showDiagram]'s KDoc). [zoomToolbar] is a
     * SIBLING of [zoomWrapper] (`stage.zoomToolbar(...)`, not `wrapper.zoomToolbar(...)`), so
     * dimming the wrapper alone never dims the toolbar -- exactly the pre-R-27 behaviour, just
     * expressed one level higher. */
    fun setDimmed(dimmed: Boolean) {
        val wrapper = zoomWrapper ?: return
        if (dimmed) wrapper.addCssClass("kportal-stage-dimmed") else wrapper.removeCssClass("kportal-stage-dimmed")
    }

    fun showEmptyStage(onExampleClick: () -> Unit) {
        stage.removeAll()
        resetZoomState()
        stage.div(className = "kportal-empty-diagram") {
            div(t.editor.emptyStageIntro)
            val chip = button(t.editor.exampleChipLabel, className = "kportal-chip")
            chip.onClick { onExampleClick() }
        }
    }

    fun applyRetryAfterCountdown(
        seconds: Long,
        onExpired: () -> Unit,
    ) {
        var remaining = seconds

        fun tick() {
            if (remaining <= 0) {
                onExpired()
                return
            }
            generateButton.text = t.editor.generateWithCountdown(remaining)
            remaining -= 1
            window.setTimeout({ tick() }, 1000)
        }
        generateButton.disabled = true
        tick()
    }

    fun setGenerating(value: Boolean) {
        generating = value
        generateButton.disabled = value || promptTextArea.value.isNullOrBlank()
        cancelButton.visible = value
        if (value) {
            generateButton.text = t.editor.generating
            setDimmed(true)
            setStatusNeutral(t.editor.generating)
        } else {
            generateButton.text = t.editor.generate
            setDimmed(false)
        }
    }

    /**
     * R-22: the "first generation names your thing" magic now targets TWO independent names --
     * the diagram's own tab name (primary, since a project can hold several) and, only under the
     * narrower condition below, the project's name too.
     *
     * The diagram half fires whenever [currentDiagramName] still looks like the server-assigned
     * default ("Diagramm N") -- a lightweight client-side mirror of
     * `DiagramDefaultName`'s naming scheme, good enough to decide "has anyone touched this tab's
     * name yet" without a dedicated flag.
     *
     * The project half is UNCHANGED from before R-22 (still gated on [manuallyRenamed] and
     * [currentProjectName] alone) -- a project with several diagrams could in principle have its
     * name re-derived by a generation in a later diagram if it is still untouched, a narrow edge
     * case accepted for this wave rather than threading an extra "is this the project's only
     * diagram" check through the auto-name path.
     */
    fun applyAutoName(
        dsl: String,
        prompt: String,
    ) {
        val title = ProjectNameDeriver.extractDiagramTitle(dsl)
        val derived = ProjectNameDeriver.derive(title, prompt) ?: return

        if (isDefaultDiagramTabName(currentDiagramName)) {
            currentDiagramName = derived
            AppScope.launch {
                // Silent, non-critical: a failed auto-rename just leaves the default name in place.
                api.renameDiagram(diagramId, derived)
                diagramTabsHandle.reload()
            }
        }

        if (!manuallyRenamed && currentProjectName == DEFAULT_PROJECT_NAME) {
            currentProjectName = derived
            nameDiv.content = derived
            nameDiv.addCssClass("kportal-fade-in")
            AppScope.launch {
                api.renameProject(projectId, derived)
            }
        }
    }

    /** Clears the attached-context chip and local state -- called both when the user explicitly
     * removes the attachment and when the server reports it is no longer resolvable (see
     * `runGeneration`'s `ai_context_unavailable` handling below). Never itself calls the server's
     * `DELETE /api/ai/context/{id}` -- callers that need that do it themselves (see the remove
     * button's click handler), since in the `ai_context_unavailable` case there is nothing left on
     * the server to delete. */
    fun clearAttachedContext() {
        attachedContextId = null
        attachedContextLabel = null
        attachChipRow.removeAll()
    }

    /** Renders the attached-file confirmation chip -- built ENTIRELY out of KVision child-widget
     * calls (`div { span(...); button(...) }`), never `content =`/`content +=` mixed with child
     * widgets in the same container (this codebase's binding convention: the two APIs mutate the
     * container in incompatible ways and must never be combined on one node). [attachChipRow]
     * itself carries no `content` anywhere in this file -- only child widgets. */
    fun applyAttachedContext(
        contextId: String,
        label: String,
    ) {
        attachedContextId = contextId
        attachedContextLabel = label
        attachChipRow.removeAll()
        attachChipRow.div(className = "kportal-chip") {
            span(label)
            val removeChip = button("×", className = "kportal-chip-remove")
            removeChip.setAttribute("aria-label", t.editor.attachChipRemoveAriaLabel)
            removeChip.onClick {
                val idToDelete = attachedContextId
                clearAttachedContext()
                if (idToDelete != null) {
                    // Fire-and-forget: the server side is already the source of truth for
                    // resolvability (see runGeneration's ai_context_unavailable handling), so a
                    // failed delete here has no user-visible consequence beyond the TTL eventually
                    // reclaiming the entry server-side.
                    AppScope.launch { api.deleteAiContext(idToDelete) }
                }
            }
        }
    }

    // ---------- R-9: report-as-incorrect + comment thread ----------

    /** Renders [comments] into [reportThreadRow] plus a reply field -- entirely out of child
     * widgets, never `content =`/`content +=` mixed with them on the same container (this
     * codebase's binding convention). */
    fun renderThread(comments: List<GenerationCommentResponse>) {
        reportThreadRow.removeAll()
        val generationId = currentGenerationId ?: return
        comments.forEach { comment ->
            val isAdmin = comment.authorRole == "admin"
            // `authorRole` itself is server-derived and not attacker-controlled (see
            // `GenerationHistoryService.addUserComment`/`report`, which hard-code
            // `CommentAuthorRole.USER`). The label text alone, however, is NOT a reliable
            // distinguishing signal: nothing stops a regular member from registering with
            // `displayName = "kUML-Team"` (no reserved-name/blocklist check in
            // `UserService.register`), which would render identically to a real admin reply.
            // The dedicated `kportal-report-comment-admin` class below is the actual
            // distinguishing signal -- it cannot be spoofed by a display name because it is
            // driven solely by the server-verified `authorRole`.
            val authorLabel = if (isAdmin) t.editor.adminAuthorLabel else comment.authorDisplayName ?: t.editor.formerMember
            val commentClassName =
                if (isAdmin) "kportal-report-comment kportal-report-comment-admin" else "kportal-report-comment"
            reportThreadRow.div(className = commentClassName) {
                div(className = "kportal-card-meta") {
                    span(authorLabel)
                    // R-18 §5.11: a real child element instead of the pre-R-18 CSS
                    // `::after { content: "Verifiziert" }` -- generated pseudo-element content
                    // cannot follow a language switch, and is invisible to many screen readers, so
                    // this is also an accessibility improvement, not just an i18n fix. The CSS rule
                    // now targets this class directly and no longer declares `content`.
                    if (isAdmin) span(t.editor.verifiedBadge, className = "kportal-report-verified")
                }
                div(comment.body, className = "kportal-report-comment-body")
            }
        }
        reportThreadRow.div(className = "kportal-field") {
            val replyInput = textAreaInput(rows = 2, className = "kportal-textarea")
            replyInput.placeholder = t.editor.replyPlaceholder
            val sendButton = button(t.editor.sendReply, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
            sendButton.onClick {
                val text = replyInput.value?.trim().orEmpty()
                if (text.isEmpty() || !GenerationReportText.isSubmittable(text)) return@onClick
                AppScope.launch {
                    when (val result = api.addGenerationComment(generationId, text)) {
                        is ApiResult.Success -> {
                            setStatusSuccess(t.editor.replySent)
                            val updated = api.listGenerationComments(generationId)
                            if (updated is ApiResult.Success) renderThread(updated.value)
                        }
                        is ApiResult.HttpError -> {
                            val copy =
                                ErrorCopy.forHttpStatus(
                                    status = result.status,
                                    serverCode = result.error?.code,
                                    serverMessage = result.error?.message,
                                    retryAfterSeconds = null,
                                    aiLimit = null,
                                    resetLabel = null,
                                    strings = t.errors,
                                )
                            setStatusError(copy.message, copy.detail)
                        }
                        ApiResult.NetworkError -> setStatusError(ErrorCopy.forNetworkError(t.errors).message, null)
                    }
                }
            }
        }
    }

    fun loadAndRenderThread() {
        val generationId = currentGenerationId ?: return
        AppScope.launch {
            when (val result = api.listGenerationComments(generationId)) {
                is ApiResult.Success -> renderThread(result.value)
                else -> {} // Non-critical -- the thread just stays empty; the report itself already succeeded.
            }
        }
    }

    /** Rebuilds [reportRow] (and, if [reported], loads the comment thread) for the CURRENT
     * generation. `generationId == null` shows [ReportAvailability]'s explanatory hint instead of
     * the melde-Button (R-25) -- no diagram generated yet, or the history write failed
     * server-side (see `GenerateDiagramResponse.generationId`'s KDoc). Before R-25 this left
     * [reportRow] silently empty, indistinguishable from the feature simply not existing. */
    fun applyGeneration(
        generationId: String?,
        reported: Boolean,
    ) {
        currentGenerationId = generationId
        currentGenerationReported = reported
        reportRow.removeAll()
        reportFormRow.removeAll()
        reportThreadRow.removeAll()
        if (generationId == null) {
            // R-25: ruhiger, erklärender Text an genau der Stelle, an der sonst der Melde-Button
            // steht -- kein Dialog, keine Warnfarbe, keine aria-live-Region (das ist kein
            // Aktionsergebnis, sondern eine Zustandsbeschreibung; die Statuszeile darüber bleibt
            // davon unberührt). Direkter `generationId == null`-Check (statt eines
            // `ReportAvailability.hintOrNull(generationId)?.let { ... }`) -- nur so smart-castet
            // der Compiler `generationId` für den Rest der Funktion (inkl. der unten
            // verschachtelten `onClick`/`AppScope.launch`-Closures) auf `String`.
            reportRow.div(t.editor.noGenerationHint, className = "kportal-report-hint")
            return
        }

        if (reported) {
            val doneButton =
                reportRow.button(
                    t.editor.reportedButton,
                    className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto",
                )
            doneButton.disabled = true
            loadAndRenderThread()
        } else {
            val reportButton =
                reportRow.button(t.editor.reportButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
            reportButton.onClick {
                reportFormRow.removeAll()
                reportFormRow.div(className = "kportal-field") {
                    div(t.editor.reportFormLabel, className = "kportal-label")
                    val textArea = textAreaInput(rows = 3, className = "kportal-textarea")
                    val submitButton = button(t.editor.reportSubmit, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
                    val cancelButton = button(t.common.cancel, className = "kportal-btn kportal-btn-text-neutral")
                    cancelButton.onClick { reportFormRow.removeAll() }
                    submitButton.onClick {
                        val text = textArea.value?.trim().orEmpty()
                        if (!GenerationReportText.isSubmittable(text)) return@onClick
                        submitButton.disabled = true
                        AppScope.launch {
                            when (val result = api.reportGeneration(generationId, text)) {
                                is ApiResult.Success -> {
                                    setStatusSuccess(t.editor.reportThanks)
                                    applyGeneration(generationId, reported = true)
                                }
                                is ApiResult.HttpError -> {
                                    submitButton.disabled = false
                                    if (result.status == 409) {
                                        setStatusError(t.editor.alreadyReported, t.editor.alreadyReportedDetail)
                                        applyGeneration(generationId, reported = true)
                                    } else {
                                        setStatusError(t.errors.forCode(result.error?.code, result.status) ?: t.editor.reportFailed, null)
                                    }
                                }
                                ApiResult.NetworkError -> {
                                    submitButton.disabled = false
                                    setStatusError(ErrorCopy.forNetworkError(t.errors).message, null)
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fun runGeneration(prompt: String) {
        if (generating) return
        val myToken = ++generationToken
        setGenerating(true)
        AppScope.launch {
            when (val result = api.generateDiagram(diagramId, prompt, attachedContextId)) {
                is GenerateResult.Success -> {
                    if (myToken != generationToken) {
                        revokeBlobUrlIfAny(result.svgBlobUrl)
                        return@launch
                    }
                    val message = GenerateState.successMessage(diagramExistedBefore, t.editor)
                    // R-18: a successful generation consumed the prompt -- no stale draft should
                    // resurface on a later language-switch remount of this diagram.
                    PromptDraft.remember(diagramId, "")
                    // R-27: `GenerateResult.Success` carries no raw SVG text (only the blob URL,
                    // see that type's KDoc) -- shows via the `<img>` fallback until the next
                    // outline-bearing refresh (a collab MODEL_REV, a reload) makes it interactive.
                    showDiagram(result.svgBlobUrl, rawSvg = null)
                    updateDslContent(result.dsl)
                    refreshOutline()
                    diagramExistedBefore = GenerateState.afterSuccess(diagramExistedBefore)
                    setGenerating(false)
                    setStatusSuccess(message)
                    applyAutoName(result.dsl, prompt)
                    applyGeneration(result.generationId, reported = false)
                    onUsageChanged()
                }
                is GenerateResult.HttpError -> {
                    if (myToken != generationToken) return@launch
                    setGenerating(false)
                    // R-8: the attached context expired/was removed/was never ours between attach
                    // time and this generation call -- clear the chip so the UI stays honest about
                    // what the server actually still has, and stop here (this is not one of the
                    // generic ErrorCopy statuses below; the server's specific message is exact).
                    if (result.status == 400 && result.error?.code == "ai_context_unavailable") {
                        clearAttachedContext()
                        setStatusError(t.editor.contextExpired, t.editor.contextExpiredDetail)
                        return@launch
                    }
                    if (result.status == 422) {
                        diagramExistedBefore = GenerateState.afterValidationRejected(diagramExistedBefore)
                    }
                    var aiLimit: Int? = null
                    var resetLabel: String? = null
                    if (result.status == 402) {
                        // The 402 copy's "(X/X)" count and reset date come from the same usage data
                        // the header's UsageChip already fetches -- pull it here too so the approved
                        // verbatim copy can be filled in instead of falling back to the generic text.
                        val usageResult = api.usage()
                        if (myToken != generationToken) return@launch
                        if (usageResult is ApiResult.Success) {
                            aiLimit = usageResult.value.aiGenerationsLimit
                            resetLabel = UsageFormat.nextPeriodStartLabel(usageResult.value.period, t.format)
                        }
                    }
                    val copy =
                        ErrorCopy.forHttpStatus(
                            status = result.status,
                            serverCode = result.error?.code,
                            serverMessage = result.error?.message,
                            retryAfterSeconds = result.retryAfterSeconds,
                            aiLimit = aiLimit,
                            resetLabel = resetLabel,
                            strings = t.errors,
                        )
                    setStatusError(copy.message, copy.detail)
                    if (result.status == 429 && result.retryAfterSeconds != null) {
                        applyRetryAfterCountdown(result.retryAfterSeconds) {
                            generateButton.disabled = promptTextArea.value.isNullOrBlank()
                            generateButton.text = t.editor.generate
                        }
                    } else if (copy.disablesGenerateButton) {
                        generateButton.disabled = true
                    }
                }
                GenerateResult.NetworkError -> {
                    if (myToken != generationToken) return@launch
                    setGenerating(false)
                    setStatusError(ErrorCopy.forNetworkError(t.errors).message, null)
                }
            }
        }
    }

    fun startRename() {
        val original = currentProjectName
        val originalDisplay = DefaultNameDisplay.projectName(original, t.projects)
        nameDiv.content = null
        val input = nameDiv.textInput(value = original, className = "kportal-input")
        // Removing the focused input from the DOM (cancel's nameDiv.removeAll()) fires a native
        // blur event synchronously in real browsers, which would otherwise re-invoke save() with
        // the still-edited text and silently persist a rename the user just pressed Escape to
        // discard.
        var settled = false

        fun cancel() {
            if (settled) return
            settled = true
            nameDiv.removeAll()
            nameDiv.content = originalDisplay
        }

        fun save() {
            if (settled) return
            val newName = input.value?.trim().orEmpty()
            if (newName.isNotEmpty() && newName != original) {
                settled = true
                currentProjectName = newName
                manuallyRenamed = true
                nameDiv.removeAll()
                nameDiv.content = newName
                AppScope.launch {
                    when (api.renameProject(projectId, newName)) {
                        is ApiResult.Success -> {}
                        // Rename failed server-side -- revert the displayed title rather than
                        // leaving it showing a name that was never actually stored.
                        is ApiResult.HttpError, ApiResult.NetworkError -> {
                            currentProjectName = original
                            manuallyRenamed = original != DEFAULT_PROJECT_NAME
                            nameDiv.content = originalDisplay
                        }
                    }
                }
            } else {
                cancel()
            }
        }

        input.setEventListener<TextInput> {
            keydown = { e ->
                when (e.key) {
                    "Enter" -> {
                        e.preventDefault()
                        save()
                    }
                    "Escape" -> {
                        e.preventDefault()
                        cancel()
                    }
                }
            }
            blur = { save() }
        }
    }

    // ---------- Layout ----------

    div(className = "kportal-editor-subheader") {
        backButton = button(t.editor.backButton, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
        backButton.setAttribute("aria-label", t.editor.backAriaLabel)
        backButton.onClick { onBack() }
        nameDiv = div(DefaultNameDisplay.projectName(currentProjectName, t.projects), className = "kportal-project-name")
        nameDiv.onClick { if (nameDiv.content != null) startRename() }
        // R-21: "Teilen"-Button + Popover-Dialog (siehe ShareDialog.kt) -- ein Freigabe-Link
        // zeigt genau DIESES Diagramm, nie das Projekt (siehe R-21-Plan §1 fuer die Scope-
        // Begruendung).
        val shareButton = button(t.share.shareButton, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
        shareButton.setAttribute("aria-label", t.share.shareAriaLabel)
        val shareDialogHost = div()
        var shareDialogOpen = false
        shareButton.onClick {
            shareDialogHost.removeAll()
            shareDialogOpen = !shareDialogOpen
            if (shareDialogOpen) {
                shareDialogHost.shareDialog(api, diagramId, onError = { message, detail -> setStatusError(message, detail) })
            }
        }
        // Empty/hidden by default (no chip until CollabPresenceFormat.indicatorText returns
        // non-null, see the CollabClient wiring below) -- same "no chip shown" convention as the
        // usage chip, trivial here since there is no separate visibility flag to manage.
        collabIndicatorDiv = div("", className = "kportal-collab-indicator")
        // R-16: overlapping presence avatars, populated by CollabClient.onPeersChanged below.
        collabAvatarsDiv = div(className = "kportal-collab-avatars")
    }

    // R-22: the diagram-tab bar, one row below the project-name subheader -- see DiagramTabs.kt's
    // own KDoc for why a horizontal tab bar was chosen over a sidebar/dropdown.
    diagramTabsHost = div()
    diagramTabsHandle =
        diagramTabsHost.diagramTabs(
            api,
            projectId,
            diagramId,
            onSwitch = { newDiagramId -> onSwitchDiagram(newDiagramId) },
            onError = { message, detail -> setStatusError(message, detail) },
        )

    div(className = "kportal-editor-grid") {
        stage = div(className = "kportal-stage")
        div(className = "kportal-prompt-panel") {
            // R-27: the inspector panel sits ABOVE the AI-prompt column -- selecting a diagram
            // element is a more immediate, higher-priority action than composing a new prompt,
            // and this mirrors the stage/inspector pairing every legendary-UI-designer-team
            // review of this screen's layout has approved for the diagram side. `onOp` closes
            // over `collab` (a `lateinit var` assigned near the bottom of this function, see its
            // own KDoc for why that is always safe by the time a click can actually fire it).
            inspectorHost = div(className = "kportal-inspector-panel")
            inspectorHandle =
                inspectorHost.inspectorPanel(
                    strings = t.editor,
                    onOp = { op -> collab.sendOp(op) },
                    onSelectEdge = { edgeId -> setSelection(DiagramSelection.selectEdge(selection, edgeId)) },
                )

            div(t.editor.promptLabel, className = "kportal-label")
            promptTextArea = textAreaInput(rows = 5, className = "kportal-textarea")
            promptTextArea.placeholder = t.editor.promptPlaceholder

            // R-8: attach-a-context-file control. Hidden native <input type="file"> exactly per
            // ProfileScreen's established pattern -- KVision has no dedicated file-picker widget,
            // so it is created directly via document.createElement and attached to attachRow's own
            // DOM node (never innerHTML). accept/size checks here are pure convenience (see
            // AiContextFile's KDoc) -- the real validation is server-side.
            val attachRow = div(className = "kportal-attach-row")
            attachButton = attachRow.button(t.editor.attachButton, className = "kportal-btn kportal-btn-text-neutral kportal-btn-auto")
            attachRow.div(t.editor.attachHint, className = "kportal-attach-hint")
            attachFileInput = document.createElement("input") as HTMLInputElement
            attachFileInput.type = "file"
            attachFileInput.accept = AiContextFile.ACCEPT_ATTRIBUTE
            attachFileInput.style.display = "none"
            attachRow.getElement()?.appendChild(attachFileInput)
            attachChipRow = div(className = "kportal-attach-chip-row")

            div(className = "kportal-field") {
                generateButton = button(t.editor.generate, className = "kportal-btn kportal-btn-primary")
                cancelButton = button(t.common.cancel, className = "kportal-btn kportal-btn-text-neutral")
                cancelButton.visible = false
            }

            statusDiv = div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }
            statusDetailDiv = div(className = "kportal-status-detail")

            // R-9: report-as-incorrect button + (once shown) the report form / comment thread.
            // Empty/hidden until the first `applyGeneration` call -- no chip until a project's
            // most recent generation is known (see the initial-load block below).
            reportRow = div(className = "kportal-report-row")
            reportFormRow = div(className = "kportal-report-form-row")
            reportThreadRow = div(className = "kportal-report-thread")

            dslToggle = div(DslPanelState.toggleLabel(expanded = false, strings = t.editor), className = "kportal-dsl-toggle")
            dslContent = div(className = "kportal-dsl-content kportal-hidden")

            // R-26: persistent, project-scoped file store for the AI assistant -- an einklappbares
            // Panel direkt unterhalb des DSL-Panels (Entscheidung D-12: kein eigener Screen).
            projectFilesPanel(api, projectId)
        }
    }

    stage.div(className = "kportal-stage-skeleton")

    // Zoom/pan listeners live on `stage` itself (not `zoomWrapper`) and are wired exactly once,
    // from `addAfterInsertHook` so `stage`'s real DOM node is guaranteed to exist yet (KVision's
    // virtual-DOM patch may not have run synchronously by this point in the function) --
    // `stage`'s own DOM element then survives every `showDiagram()`/`showEmptyStage()` rebuild
    // (only its children are torn down), so there is no per-diagram re-wiring/teardown to manage.
    // Every handler no-ops via its own `zoomWrapper`/`isPanning` guard when there is no diagram.
    stage.addAfterInsertHook {
        stage.getElement()?.let { stageElement ->
            stageElement.addEventListener(
                "wheel",
                { e -> handleWheel(e as WheelEvent) },
                AddEventListenerOptions(passive = false),
            )
            stageElement.addEventListener("pointerdown", { e -> handlePointerDown(e as PointerEvent) })
            stageElement.addEventListener(
                "pointermove",
                { e ->
                    val pointerEvent = e as PointerEvent
                    handlePointerMove(pointerEvent)
                    // R-28: unguarded by `isPanning` (unlike handlePointerMove above) -- cursor
                    // sharing runs on every pointer move over the stage regardless of pan state.
                    handlePresencePointerMove(pointerEvent)
                },
            )
            stageElement.addEventListener("pointerup", { e -> handlePointerUp(e as PointerEvent) })
            stageElement.addEventListener("pointercancel", { e -> handlePointerUp(e as PointerEvent) })
            // R-28: the pointer left the stage entirely -- clears this connection's own presence
            // cursor for every peer immediately, rather than waiting out the idle timeout.
            stageElement.addEventListener("pointerleave", { e -> handlePresencePointerLeave(e as PointerEvent) })
            stageElement.addEventListener("dblclick", { zoomFit() })
            stageElement.addEventListener("click", { e -> handleClick(e as MouseEvent) })
        }
    }

    generateButton.disabled = true
    generateButton.onClick {
        val prompt = promptTextArea.value?.trim().orEmpty()
        if (prompt.isNotEmpty()) runGeneration(prompt)
    }
    cancelButton.onClick {
        generationToken += 1
        setGenerating(false)
        setStatusNeutral(t.editor.cancelled)
    }

    // ---------- R-8: attach-a-context-file wiring ----------
    attachButton.onClick { attachFileInput.click() }
    attachFileInput.addEventListener(
        "change",
        {
            val file: File? = attachFileInput.files?.item(0)
            attachFileInput.value = "" // allow re-selecting the same file later (matches ProfileScreen)
            if (file != null) {
                val format = AiContextFile.formatForFileName(file.name)
                if (format == null) {
                    setStatusError(t.editor.unsupportedFormat, t.editor.unsupportedFormatDetail)
                } else if (AiContextFile.tooLarge(file.size.toInt())) {
                    setStatusError(t.editor.fileTooLarge, t.editor.fileTooLargeDetail)
                } else {
                    AppScope.launch {
                        setStatusNeutral(t.editor.checkingFile)
                        when (val result = api.uploadAiContext(file, format)) {
                            is ApiResult.Success -> {
                                val label = AiContextFile.chipLabel(file.name, result.value.bytes, t.format)
                                applyAttachedContext(result.value.contextId, label)
                                setStatusNeutral(t.editor.attachedFile(label))
                            }
                            is ApiResult.HttpError -> {
                                val message = t.errors.forCode(result.error?.code, result.status) ?: t.editor.contextUploadFailed
                                setStatusError(message, null)
                            }
                            ApiResult.NetworkError -> setStatusError(ErrorCopy.forNetworkError(t.errors).message, null)
                        }
                    }
                }
            }
        },
    )

    promptTextArea.setEventListener<TextAreaInput> {
        input = {
            generateButton.disabled = generating || promptTextArea.value.isNullOrBlank()
            // R-18: keep the half-typed prompt alive across a language-switch remount (see
            // `PromptDraft.kt`'s KDoc) -- a blank value clears any previously remembered draft.
            PromptDraft.remember(diagramId, promptTextArea.value.orEmpty())
        }
        keydown = { e ->
            if ((e.ctrlKey || e.metaKey) && e.key == "Enter") {
                e.preventDefault()
                val prompt = promptTextArea.value?.trim().orEmpty()
                if (prompt.isNotEmpty()) runGeneration(prompt)
            }
        }
    }
    // R-18: restore a draft left behind by a language-switch remount of THIS diagram -- a real
    // navigation to a different diagram never sees a hit here (see `PromptDraft.consume`'s
    // diagram-id guard).
    PromptDraft.consume(diagramId)?.let { draft ->
        promptTextArea.value = draft
        generateButton.disabled = draft.isBlank()
    }
    dslToggle.onClick {
        dslExpanded = !dslExpanded
        dslToggle.content = DslPanelState.toggleLabel(dslExpanded, t.editor)
        if (dslExpanded) dslContent.removeCssClass("kportal-hidden") else dslContent.addCssClass("kportal-hidden")
        loadDslIfNeeded()
    }

    // ---------- Diagram (re)load ----------

    /**
     * Fetches and displays the project's current server-side diagram -- called once at mount, and
     * again from the collaboration connection's refresh signal (see the `CollabClient` wiring
     * below) whenever another collaborator's edit or an AI regeneration changes the diagram
     * server-side. [focusPromptOnFirstEmptyRender] is a one-shot flag, so a later call landing on
     * [RenderResult.Empty] (e.g. a collaborator just cleared the diagram) never re-steals focus
     * from whatever the user is doing. Also invalidates the R-24 DSL panel cache ([invalidateDsl])
     * -- covers ALL THREE ways the server-side diagram state can change out from under this
     * screen (initial mount, collab refresh, R-22 tab switch via remount), so the shown DSL can
     * never drift from the shown image.
     */
    suspend fun refreshDiagramFromServer() {
        invalidateDsl()
        // R-27: unconditionally, regardless of what `renderProject` below returns -- `/outline`
        // is its own independent GET, and every one of `refreshDiagramFromServer`'s three callers
        // (initial mount, collab refresh, R-22 tab switch) changed the live model just as
        // thoroughly as it changed the rendered image (see `refreshOutline`'s own KDoc).
        refreshOutline()
        when (val result = api.renderProject(diagramId)) {
            is RenderResult.Rendered -> {
                diagramExistedBefore = true
                showDiagram(result.svgBlobUrl, result.svg)
            }
            RenderResult.Empty -> {
                diagramExistedBefore = false
                showEmptyStage(onExampleClick = {
                    promptTextArea.value = t.editor.examplePrompt
                    runGeneration(t.editor.examplePrompt)
                })
                applyGeneration(null, reported = false)
                if (focusPromptOnFirstEmptyRender) {
                    focusPromptOnFirstEmptyRender = false
                    promptTextArea.addAfterInsertHook { promptTextArea.focus() }
                    promptTextArea.focus()
                }
            }
            is RenderResult.ValidationFailed -> {
                // The persisted DSL existed but no longer renders -- rare (it rendered fine when it
                // was saved). Treat as "a diagram existed before" for mustFix 1's wording purposes.
                diagramExistedBefore = true
                showEmptyStage(onExampleClick = {
                    promptTextArea.value = t.editor.examplePrompt
                    runGeneration(t.editor.examplePrompt)
                })
                setStatusError(t.editor.storedDiagramUnrenderable, result.message)
            }
            is RenderResult.HttpError -> {
                val copy =
                    ErrorCopy.forHttpStatus(
                        status = result.status,
                        serverCode = result.error?.code,
                        serverMessage = result.error?.message,
                        retryAfterSeconds = null,
                        aiLimit = null,
                        resetLabel = null,
                        strings = t.errors,
                    )
                setStatusError(copy.message, copy.detail)
            }
            RenderResult.NetworkError -> setStatusError(ErrorCopy.forNetworkError(t.errors).message, null)
        }
    }

    // ---------- Initial load ----------

    AppScope.launch {
        val projectResult = api.getProject(projectId)
        if (projectResult is ApiResult.Success) {
            currentProjectName = projectResult.value.name
            nameDiv.content = currentProjectName
            manuallyRenamed = currentProjectName != DEFAULT_PROJECT_NAME
        }

        // R-22: the diagram's OWN name (for downloads/auto-naming) -- there is no dedicated
        // "get one diagram" endpoint, so this reads it off the already-needed diagram list.
        val diagramsResult = api.listDiagrams(projectId)
        if (diagramsResult is ApiResult.Success) {
            val current = diagramsResult.value.firstOrNull { it.id == diagramId }
            if (current != null) currentDiagramName = current.name
        }

        refreshDiagramFromServer()

        // R-9: the most recent generation stays reportable across a reload, not just right after
        // it was created. R-25: the "no generation yet" case is now applied explicitly too --
        // before R-25 this branch simply left the row untouched, indistinguishable from the
        // button silently not existing. On a FAILED list request (HttpError/NetworkError),
        // deliberately apply NOTHING: whether a generation exists is unknown, and the R-25 hint
        // "erst nach einer KI-Generierung möglich" could be an outright false statement then.
        val generationsResult = api.listGenerations(diagramId, limit = 1)
        val latest = (generationsResult as? ApiResult.Success)?.value?.firstOrNull()
        if (latest != null) {
            applyGeneration(latest.id, latest.reported)
        } else if (generationsResult is ApiResult.Success) {
            applyGeneration(null, reported = false)
        }
    }

    // ---------- Live collaboration ----------

    // Same-account-only, honest v1 collaboration (see CollabClient's KDoc): joins this project's
    // collab room, re-fetches the diagram whenever a CRDT refresh signal arrives (never parses the
    // CRDT bytes itself), and shows a trivial "N andere Verbindungen aktiv" indicator, ALONGSIDE
    // R-27's interactive-editing traffic (`sendOp`/`onModelOpRejected`, see CollabClient's own
    // KDoc). Assigned to the `lateinit var collab` declared near the top of this function (not a
    // fresh `val`) -- see that declaration's own KDoc for why. Instantiated after the layout above
    // so collabIndicatorDiv already exists for onPeerCountChanged's very first (possibly
    // synchronous) callback.
    collab =
        CollabClient(
            diagramId = diagramId,
            onRefreshNeeded = { AppScope.launch { refreshDiagramFromServer() } },
            onPeersChanged = { peers ->
                latestPeers = peers
                renderCollabAvatars()
            },
            onModelOpRejected = { reject ->
                // Design-team ruling ("abgelehnte Op = Statuszeile, Verbindung bleibt offen") --
                // a status-line message, nothing else: no modal, no toast, the connection and the
                // current selection/inspector state are untouched.
                setStatusError(ModelOpFeedback.messageFor(reject.reason, t.editor), null)
            },
            onPeerPresenceChanged = { tokenHex, present, rawPayload ->
                val previousSelection = presenceState[tokenHex]?.selection.orEmpty()
                presenceState =
                    if (present) {
                        val decoded = PresencePayload.decode(rawPayload, currentViewBoxRect(), allIdsOf(latestOutline))
                        PresenceState.withUpdate(presenceState, tokenHex, decoded, jsNowMillis())
                    } else {
                        PresenceState.withoutPeer(presenceState, tokenHex)
                    }
                repaintPresenceOverlay()
                // R-28: the avatar strip (title tooltip + indicator count) only needs rebuilding on
                // an actual SELECTION change for this peer -- never on the 10 Hz cursor-only stream
                // (see `renderCollabAvatars`'s own KDoc "Stolperfallen").
                val newSelection = presenceState[tokenHex]?.selection.orEmpty()
                if (newSelection != previousSelection) renderCollabAvatars()
            },
        )
    collab.connect()

    // R-28: a lightweight 1s sweep so a stale peer cursor visually disappears via
    // `PresenceState.visibleCursorOf`'s TTL even without any NEW presence frame arriving to trigger
    // a repaint (the "closed the laptop lid" case, see that function's own KDoc).
    presenceTickTimer = window.setInterval({ repaintPresenceOverlay() }, 1000)

    return EditorScreenHandle {
        cancelPendingPresenceTimer()
        clearPresenceIdleTimer()
        presenceTickTimer?.let { window.clearInterval(it) }
        presenceTickTimer = null
        // Best-effort: clears this connection's presence for every remaining peer immediately,
        // rather than waiting for the server to eventually notice the socket closed.
        collab.sendPresence(PresencePayload.EMPTY)
        collab.close()
        revokeBlobUrlIfAny(currentSvgBlobUrl)
    }
}
